import type { Action, Thunk } from 'types/redux'
import type { TrackId } from 'types/track'
import {
  FORM_PLAYLIST_SHARE_RESET,
  FORM_PLAYLIST_SHARE_UPDATE,
  FORM_REFERRAL_INVITE_RESET,
  FORM_REFERRAL_INVITE_UPDATE,
  FORM_TRACK_MERGE_CONFIRM,
  FORM_TRACK_MERGE_RESET,
  FORM_TRACK_MERGE_SELECT_ORIGINAL_FORMAT,
  PRICING_UPDATE,
} from '@disco/constants/ActionTypes'
import type { PlaylistShareForm } from '@disco/state-management/reducers/forms/playlistShare'
import type { PricingFormState } from '@disco/state-management/reducers/forms/pricing'
/**
 * Resets the playlist share form to its initial state.
 * @returns Action object to reset the playlist share form
 */
export const resetPlaylistShareForm = () => ({
  type: FORM_PLAYLIST_SHARE_RESET,
})
/**
 * Updates specific fields in the playlist share form.
 * @param fields - Partial form data to update
 * @returns Action object with updated form fields
 */
export const updatePlaylistShareForm = (
  fields: Partial<PlaylistShareForm>,
) => ({
  type: FORM_PLAYLIST_SHARE_UPDATE,
  fields,
})
/**
 * Resets the referral invite form to its initial state.
 * @returns Action object to reset the referral invite form
 */
export const resetReferralInviteForm = (): Action => ({
  type: FORM_REFERRAL_INVITE_RESET,
})
/**
 * Updates specific fields in the referral invite form.
 * @param fields - Partial form data to update
 * @returns Action object with updated form fields
 */
export const updateReferralInviteForm = (
  fields: Partial<PlaylistShareForm>,
) => ({
  type: FORM_REFERRAL_INVITE_UPDATE,
  fields,
})
/**
 * Resets the track merge form to its initial state.
 * @returns Action object to reset the track merge form
 */
export const resetTrackMergeForm = (): Action => ({
  type: FORM_TRACK_MERGE_RESET,
})
/**
 * Selects the original format track in the track merge form.
 * @param originalFormatTrackId - The ID of the track to set as original format
 * @returns Action object with selected original format track ID
 */
export const selectOriginalFormatInTrackMergeForm = (
  originalFormatTrackId: TrackId,
) => ({
  type: FORM_TRACK_MERGE_SELECT_ORIGINAL_FORMAT,
  originalFormatTrackId,
})
/**
 * Confirms the track merge operation.
 * @returns Action object to confirm track merge
 */
export const confirmTrackMerge = (): Action => ({
  type: FORM_TRACK_MERGE_CONFIRM,
})
/**
 * Updates specific fields in the pricing form.
 * @param fields - Partial pricing form data to update
 * @returns Action object with updated pricing form fields
 */
export const updatePricingForm = (fields: Partial<PricingFormState>) => ({
  type: PRICING_UPDATE,
  fields,
})
/**
 * Changes the active tab in the plans section and resets plan selection.
 * @param newActiveIndex - The index of the new active tab
 * @returns Thunk that updates the active plans tab and resets plan selection
 */
export const changePlansTab =
  (newActiveIndex: number): Thunk =>
  (dispatch, getState) => {
    const state = getState()
    const plansTabs = state.forms.pricingForm.plansTabs

    if (plansTabs.active === newActiveIndex) {
      return
    }

    dispatch({
      type: PRICING_UPDATE,
      fields: {
        plan: null,
        plansTabs: { ...plansTabs, active: newActiveIndex },
      },
    })
  }
